import type { ComponentPlacementLocation } from "@intelligems/ig-types/dtos/components";
import type { InsertType } from "@intelligems/ig-types/dtos/onsite-edits";
import type {
  OnsiteUpsellProductSelection,
  UpsellCondition,
  UpsellNumericOperator,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import type { UpsellDiscountType } from "@intelligems/ig-types/dtos/upsell-product-rules";
import {
  UpsellNumericOperatorList,
  UpsellPriceOperatorList,
} from "@intelligems/ig-types/schemas/visual-builder/onsite-upsell-rules";
import type { PlacementMode } from "@src/store/slices/experienceBuilder/types";

// The "anything" pseudo-type isn't a real `UpsellCondition` — it's how the UI
// represents a rule with no conditions (always-on). Selecting a real type turns
// it into a condition; removing the last condition returns the rule to it.
export type ConditionType = UpsellCondition["type"] | "anything";

export const PLACEMENT_LOCATION_LABELS: Record<
  ComponentPlacementLocation,
  string
> = {
  pdp: "Product page (PDP)",
  cart: "Cart",
};

// The two ways an upsell picks its spot. "Theme block" is the durable one —
// the merchant owns the position in the theme editor, so a theme update can't
// silently move or orphan the placement the way a renamed class can.
export const PLACEMENT_MODE_LABELS: Record<PlacementMode, string> = {
  themeBlock: "Theme block",
  selector: "CSS selector",
};

export const DISCOUNT_TYPE_LABELS: Record<UpsellDiscountType, string> = {
  none: "No discount",
  percentage: "Percentage off",
  fixed: "Fixed amount off",
};

export const INSERT_TYPE_LABELS: Record<InsertType, string> = {
  beforebegin: "Before the element",
  afterbegin: "Inside, at the start",
  beforeend: "Inside, at the end",
  afterend: "After the element",
};

// Condition types offered in the "IF" dropdown, per placement location. Mirrors
// the schema's `refineOnsiteUpsellRules` split (cart-only vs product-only).
export const CONDITION_TYPES: Record<
  ComponentPlacementLocation,
  { value: ConditionType; label: string; disabled?: boolean }[]
> = {
  pdp: [
    { value: "anything", label: "Viewing any product" },
    { value: "products", label: "Specific products" },
    { value: "collection", label: "Products from collection" },
    { value: "tag", label: "Products with tag" },
    { value: "productPrice", label: "Product price" },
  ],
  cart: [
    { value: "anything", label: "Any cart" },
    { value: "collection", label: "Product in collection" },
    { value: "tag", label: "Product with tag" },
    { value: "cartSubtotal", label: "Cart subtotal" },
    { value: "cartItemCount", label: "Cart item count" },
  ],
};

// The same in/out toggle, worded to fit each surface.
export const MEMBERSHIP_OPTIONS: Record<
  ComponentPlacementLocation | "thenShow",
  { value: boolean; label: string }[]
> = {
  pdp: [
    { value: true, label: "Is" },
    { value: false, label: "Is not" },
  ],
  cart: [
    { value: true, label: "Are in cart" },
    { value: false, label: "Are not in cart" },
  ],
  thenShow: [
    { value: true, label: "Including" },
    { value: false, label: "Excluding" },
  ],
};

const NUMERIC_OPERATOR_LABELS: Record<UpsellNumericOperator, string> = {
  greaterThan: "Is greater than",
  lessThan: "Is less than",
  equalTo: "Is equal to",
  greaterThanOrEqual: "Is greater than or equal to",
  lessThanOrEqual: "Is less than or equal to",
  between: "Is between",
};

export const NUMERIC_OPERATOR_OPTIONS = UpsellNumericOperatorList.map((op) => ({
  value: op,
  label: NUMERIC_OPERATOR_LABELS[op],
}));

// Product price persists through the narrower `ProductFilterList`, so it only
// offers the two operators that layer can express.
export const PRICE_OPERATOR_OPTIONS = UpsellPriceOperatorList.map((op) => ({
  value: op,
  label: NUMERIC_OPERATOR_LABELS[op],
}));

export const PRODUCT_SOURCE_OPTIONS: {
  value: OnsiteUpsellProductSelection["source"];
  label: string;
}[] = [
  { value: "recommendations", label: "Shopify recommendations" },
  { value: "products", label: "Specific products" },
  { value: "collection", label: "Products from collection" },
  { value: "tag", label: "Products with tag" },
];

// A fresh condition of the chosen type with schema-valid starter values.
export function createCondition(type: ConditionType): UpsellCondition | null {
  switch (type) {
    case "anything":
      return null;
    case "products":
      return { type: "products", include: true, productIds: [] };
    case "variants":
      return { type: "variants", include: true, variants: [] };
    case "collection":
      return { type: "collection", include: true, collectionId: "" };
    case "tag":
      return { type: "tag", include: true, tag: "" };
    case "cartSubtotal":
      return { type: "cartSubtotal", operator: "greaterThan", value: 0 };
    case "cartItemCount":
      return { type: "cartItemCount", operator: "greaterThan", value: 0 };
    case "productPrice":
      return { type: "productPrice", operator: "greaterThan", value: 0 };
  }
}

// A fresh product source of the chosen kind.
export function createProductSource(
  source: OnsiteUpsellProductSelection["source"]
): OnsiteUpsellProductSelection {
  switch (source) {
    case "recommendations":
      return { source: "recommendations" };
    case "products":
      return { source: "products", products: [] };
    case "collection":
      return { source: "collection", include: true, collectionId: "" };
    case "tag":
      return { source: "tag", include: true, tag: "" };
  }
}
